/**
 * @file useResize
 * @author gaoya10
 */

import {isEqual} from 'lodash';
import React, {useState, useRef, useEffect, useMemo, useCallback} from 'react';
import {addListener, removeListener} from 'resize-detector';

interface Size {
    width: number,
    height: number
}
type Arg = HTMLElement | React.MutableRefObject<any>;

// 现象： 接收的参数（useRef创建的变量）直接作为useMemo的依赖，{current: null} -> {current: div}变化未触发useMemo的执行
// 分析：依赖未发生变化（此例子又不能直接将arg.current直接做依赖，因为arg可能不存在） 待调研
// 处理方式：将参数arg放在数组，监听数组args，可以监听到[{curent: null}] -> [{curent: div}]的变化
const useResize = (...args: [Arg?]): Size => {
    const [size, setSize] = useState<Size>({width: 0, height: 0});
    const resizeRef = <any>useRef(null);

    const element: HTMLElement | null = useMemo(() => {
        if (!args.length) {
            return null;
        }
        else if (React.isValidElement(args[0])) {
            return args[0];
        }
        else if ((args[0] as any).current) {
            return (args[0] as any).current;
        }
        return null;
    }, [args]);

    const setNewSize = useCallback(newSize => {
        if (isEqual(newSize, resizeRef.current)) {
            return;
        }
        resizeRef.current = newSize;
        setSize(resizeRef.current);
    }, [resizeRef, setSize]);
    // 元素监听
    const getElementSize = useCallback(() => {
        const {offsetWidth: width, offsetHeight: height} = (element as any);
        const currentSize = {width, height};
        setNewSize(currentSize);
    }, [element, setNewSize]);
    // window监听
    const getWindowSize = useCallback(() => {
        const currentSize = {width: window.outerWidth, height: window.outerHeight};
        setNewSize(currentSize);
    }, [setNewSize]);

    useEffect(() => {
        if (element) {
            getElementSize();
            addListener(element, getElementSize);
        }
        else {
            getWindowSize();
            window.addEventListener('resize', getWindowSize);
        }
        return () => {
            // 依赖变化每次都会执行
            element ? removeListener(element, getElementSize)
                : window.removeEventListener('resize', getWindowSize);
        };
    }, [element, getElementSize, getWindowSize]);

    return size;
};

export default useResize;